
import Themeable from 'vuetify/lib/mixins/themeable';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import DNSRepositoryInterface, { DNSZonesInterface } from '@/services/DNS/Interfaces';
import DeleteDnsZoneDialog from '@/components/dialogs/dns/DeleteDnsZoneDialog.vue';
import SyncDNSDialog from '@/components/dialogs/dns/SyncDNSDialog.vue';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import SyncServerDNSDialog from '@/components/dialogs/dns/SyncServerDNSDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [Themeable],
  inject: [
    'dnsRepository',
    'serverRepository',
  ],
  components: {
    DeleteDnsZoneDialog,
    SyncDNSDialog,
    SyncServerDNSDialog,
    PageHeader,
  },
})
export default class AllDns extends Vue {
  public loading: boolean = false;

  public headers: DataTableHeader[] = [
    { text: 'Website', value: 'websiteId' },
    { text: 'Domain', value: 'installation.domain' },
    { text: 'Server', value: 'installation.server' },
    { text: 'Server IP', value: 'installation.dnsIP' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public tableSearch: string = '';
  public zoneSearch: string = '';
  public options: ListPaginationOptions = defaultListOptions();
  public readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public readonly dnsRepository!: DNSRepositoryInterface;

  public dnsZones: DNSZonesInterface[] = [];
  public deleteDnsZoneDialogData: DNSZonesInterface | false = false;
  public deleteDnsZoneDialog: string = '';

  public syncDNSDialog: string = '';

  public syncServerDNSDialog: string = '';
  public serversForSyncDialog: ServerInterface[] = [];

  public readonly serverRepository!: ServerRepositoryInterface;
  public serversMap: { [id: string]: string } = {};

  public created(): void {
    this.applyRouteQuery();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'dns_zones',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  public async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  public onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'dns_zones') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  public async search(ignoreErrors: boolean = false): Promise<void> {
    this.loading = true;
    if (this.zoneSearch.length < 3) {
      this.$errorDialog.show('Please be more specific about your search!');
      return;
    }

    try {
      this.dnsZones = await this.dnsRepository.searchZones(this.zoneSearch);
      if (this.dnsZones.length) {
        const servers = await this.serverRepository.getServersByIds(
            this.dnsZones.map((zone) => zone.installation.server),
        );

        servers.forEach((server) => {
          this.serversMap[server._id] = server.hostname;
        });
      }
      this.loading = false;
    } catch (e) {
      this.dnsZones = [];

      if (!ignoreErrors) {
        if (e.response.data) {
          this.$errorDialog.show(e.response.data.message);
        } else {
          this.$errorDialog.show(e.message);
        }
      }
    }
  }

  public createZone(): void {
    this.$router.push({ name: 'dns_zone_create' });
  }

  public editZone(): void {
    console.log('Edit DNS zone');
  }

  public async deleteZonePrompt(zone: DNSZonesInterface): Promise<void> {
    this.deleteDnsZoneDialog = 'delete-dns-zone-dialog';
    this.deleteDnsZoneDialogData = zone;
  }

  public async deleteZone(zone: DNSZonesInterface): Promise<void> {
    try {
      await this.dnsRepository.deleteDNSZone(zone.id);

      this.$successDialog.show(`DNS Zone has been removed.`);
      await this.search(true);
    } catch (e) {
      this.$errorDialog.show(`Error deleting dns zone: ${e.message}`);
    }
  }

  public async showSyncDialog(): Promise<void> {
    this.syncDNSDialog = 'SyncDNSDialog';
  }

  public async syncDNS(step: number): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(undefined, undefined, step);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }

  public async showSyncServerDialog(): Promise<void> {
    try {
      this.serversForSyncDialog = await this.serverRepository.getAllServers();
      this.syncServerDNSDialog = 'SyncServerDNSDialog';
    } catch (err) {
      this.$errorDialog.show('Failed to load servers for sync dialog!');
    }
  }

  public async syncServerDNS(server: string, step: number): Promise<void> {
    try {
      await this.dnsRepository.syncDNS(undefined, server, step);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.message || err.message);
    }
  }
}
